import React, { useEffect, useState } from "react";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../../components/Tenant/DataTableComponent/DataTableComponent";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { CircleLoaderComponent } from "../../../../components/Loader/CircleLoaderComponent";
import {
  useAddZoneMasterMutation,
  useDeleteZoneMasterMutation,
  useFetchZoneMasterListMutation,
  useUpdateZoneMasterMutation,
  useUploadZoneMasterCsvMutation,
} from "../../../../features/tenant/masters/zoneStateMasterApi";
import { downloadSampleCSV } from "../../../../utils/downloadSampleCsv";
import { parseCSV } from "../../../../utils/parseCsv";
import { downloadJsonAsCsv } from "../../../../utils/downloadJsonAsCsv";

const ZONE_NAME_MAX_LENGTH = 10;
const initialFilters = { zone: "", status: "" };
const initialForm = { id: null, zone: "", status: "1", location_count: 0 };
const zoneCsvTemplate = [{ name: "zone", label: "Sample Zone", maxLength: ZONE_NAME_MAX_LENGTH }];

const inputClass =
  "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500";

const ZoneMasterList = ({ embedded = false }) => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const [zoneList, setZoneList] = useState([]);
  const [total, setTotal] = useState(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addTab, setAddTab] = useState("manual");
  const [csvFile, setCsvFile] = useState(null);

  const [fetchZoneList, { isLoading: listLoading }] = useFetchZoneMasterListMutation();
  const [addZone, { isLoading: addLoading }] = useAddZoneMasterMutation();
  const [updateZone, { isLoading: updateLoading }] = useUpdateZoneMasterMutation();
  const [deleteZone, { isLoading: deleteLoading }] = useDeleteZoneMasterMutation();
  const [uploadZoneCsv, { isLoading: csvUploading }] = useUploadZoneMasterCsvMutation();
  const [fetchZoneListForExport, { isLoading: exportLoading }] = useFetchZoneMasterListMutation();

  const isEdit = Boolean(form.id);
  const isSaving = addLoading || updateLoading;

  const authParams = () => ({
    token: sessionStorage.getItem("access_token"),
    tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
  });

  const loadZones = async (appliedFilters = filters, pageLimit = limit, pageOffset = offset) => {
    try {
      const res = await fetchZoneList({
        ...authParams(),
        body: { ...appliedFilters, limit: pageLimit, offset: pageOffset },
      }).unwrap();
      setZoneList(res?.body?.rows || []);
      setTotal(res?.body?.total || 0);
    } catch (error) {
      toast.error(error?.data?.message || "Error fetching zone list");
    }
  };

  useEffect(() => {
    loadZones();
  }, [limit, offset]);

  const handlePageChange = (pageNumber) => {
    setOffset((pageNumber - 1) * limit);
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
  };

  const applyFilters = (appliedFilters) => {
    setCurrentPage(1);
    if (offset === 0) {
      loadZones(appliedFilters, limit, 0);
    } else {
      setOffset(0);
    }
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    applyFilters(initialFilters);
  };

  const openAddModal = () => {
    setForm(initialForm);
    setAddTab("manual");
    setCsvFile(null);
    setShowFormModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      zone: row.zone || "",
      status: String(row.status) === "2" ? "2" : "1",
      location_count: row.location_count || 0,
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setForm(initialForm);
    setAddTab("manual");
    setCsvFile(null);
  };

  const handleCsvSubmit = () => {
    if (!csvFile) {
      toast.error("Please select a file");
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async (event) => {
      const rows = parseCSV(event.target.result);
      try {
        const res = await uploadZoneCsv({ ...authParams(), body: { rows } }).unwrap();
        const insertedCount = res?.body?.inserted?.length || 0;
        const skippedCount = res?.body?.skipped?.length || 0;
        toast.success(`${insertedCount} zone(s) added, ${skippedCount} skipped`);
        closeFormModal();
        loadZones();
      } catch (error) {
        toast.error(error?.data?.message || "Error uploading zone CSV");
      }
    };
    fileReader.readAsText(csvFile);
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetchZoneListForExport({ ...authParams(), body: { limit: 100000, offset: 0 } }).unwrap();
      const rows = res?.body?.rows || [];
      if (rows.length === 0) {
        toast.error("No zones to export");
        return;
      }
      downloadJsonAsCsv(rows, "zone_master.csv");
    } catch (error) {
      toast.error(error?.data?.message || "Error exporting zone CSV");
    }
  };

  const handleSave = async () => {
    const zoneName = form.zone.trim().toUpperCase();

    if (!zoneName) {
      toast.error("Zone name is required");
      return;
    }

    try {
      if (isEdit) {
        await updateZone({ ...authParams(), body: { id: form.id, zone: zoneName, status: form.status } }).unwrap();
        toast.success("Zone updated successfully");
      } else {
        await addZone({ ...authParams(), body: { zone: zoneName } }).unwrap();
        toast.success("Zone added successfully");
      }
      closeFormModal();
      loadZones();
    } catch (error) {
      toast.error(error?.data?.message || (isEdit ? "Error updating zone" : "Error adding zone"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteZone({ ...authParams(), body: { id: deleteTarget.id } }).unwrap();
      toast.success("Zone deleted successfully");
      setDeleteTarget(null);

      // If the last row of a later page was deleted, step back one page.
      if (zoneList.length === 1 && offset > 0) {
        setOffset(Math.max(0, offset - limit));
        setCurrentPage((page) => Math.max(1, page - 1));
      } else {
        loadZones();
      }
    } catch (error) {
      setDeleteTarget(null);
      toast.error(error?.data?.message || "Error deleting zone", { duration: 6000 });
    }
  };

  const columns = [
    { name: "SNo", cell: (row, idx) => offset + Number(idx + 1), width: "80px" },
    { name: "Zone", selector: (row) => row.zone || "-" },
    {
      name: "Status",
      cell: (row) =>
        String(row.status) === "1" ? (
          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Active</span>
        ) : (
          <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Inactive</span>
        ),
    },
    { name: "Zone Locations", selector: (row) => row.location_count ?? 0 },
    { name: "Created By", selector: (row) => row.created_by_name || "-" },
    {
      name: "",
      minWidth: "180px",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEditModal(row)}
            className="flex justify-center items-center rounded px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out"
            style={{ backgroundColor: primaryColor?.["700"] || "#2563eb" }}
          >
            Edit
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="flex justify-center items-center rounded bg-red-600 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const content = (
    <div className="p-3">
      <div className="p-3 w-full bg-white shadow rounded-lg">
        <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
          <div className="flex space-x-2 justify-end">
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">Zone</label>
                <input
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.zone}
                  onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
                />
              </div>
            </div>
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">Status</label>
                <select
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="1">Active</option>
                  <option value="2">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex flex-row gap-2 h-10 mt-6">
              <button className="btn btn-outline-primary p-2" onClick={() => applyFilters(filters)}>
                Search
              </button>
              <button className="btn btn-outline-primary p-2" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <DataTableComponent
          keyField="id"
          columns={columns}
          data={zoneList}
          totalRows={total}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          paginationDefaultPage={currentPage}
        />

        <div className="fixed bottom-0 right-10 w-16 h-16 mr-10 mb-6 cursor-pointer" id="box_btn">
          <div className="text-right">
            <Menu className="relative inline-block text-left">
              <div>
                <Menu.Button
                  onClick={openAddModal}
                  className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                  style={{ backgroundColor: `${primaryColor["700"]}` }}
                >
                  <HiOutlinePlus className="w-full h-full p-3" />
                </Menu.Button>
              </div>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {(listLoading || isSaving || deleteLoading || exportLoading) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <CircleLoaderComponent />
        </div>
      )}

      {embedded ? (
        content
      ) : (
        <div className="flex w-full bg-gray-200 h-full justify-center">
          <div className="w-full">
            <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
              Zone List
              <LinksComponent />
            </div>
            {content}
          </div>
        </div>
      )}

      {showFormModal && (
        <ModalComponent show={showFormModal} setShow={closeFormModal} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">{isEdit ? "Edit Zone" : "Add Zone"}</h2>

            {!isEdit && (
              <div className="flex justify-center gap-2 border-b">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "manual"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("manual")}
                >
                  Add Manually
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "csv"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("csv")}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "export"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("export")}
                >
                  Export CSV
                </button>
              </div>
            )}

            {isEdit || addTab === "manual" ? (
              <>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Zone Name *</label>
                    <input
                      type="text"
                      maxLength={ZONE_NAME_MAX_LENGTH}
                      value={form.zone}
                      onChange={(e) => setForm({ ...form, zone: e.target.value.toUpperCase() })}
                      placeholder="e.g. NORTH"
                      className={inputClass}
                    />
                    <p className="mt-1 text-xs text-gray-500">Saved in upper case, up to {ZONE_NAME_MAX_LENGTH} characters.</p>
                  </div>

                  {isEdit && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select
                        className={inputClass}
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                      >
                        <option value="1">Active</option>
                        <option value="2">Inactive</option>
                      </select>
                    </div>
                  )}

                  {isEdit && form.location_count > 0 && (
                    <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md p-2">
                      This zone is used by {form.location_count} zone location(s). Changes apply to all of them.
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !form.zone.trim()}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      isSaving || !form.zone.trim() ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isEdit ? "Update" : "Add"}
                  </button>
                </div>
              </>
            ) : addTab === "csv" ? (
              <div className="flex flex-col gap-3">
                <div className="flex justify-center">
                  <button
                    type="button"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                    onClick={() => downloadSampleCSV(zoneCsvTemplate)}
                  >
                    Sample CSV Download
                  </button>
                </div>

                <label
                  htmlFor="zone-csv-dropzone"
                  className="flex flex-col items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <p className="text-sm text-gray-500 text-center">
                    <span className="font-semibold">Choose CSV file</span>
                    <div>Click to upload or drag and drop</div>
                  </p>
                  <input
                    id="zone-csv-dropzone"
                    type="file"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                  />
                </label>
                {csvFile && <p className="text-xs text-gray-500 text-center">{csvFile.name}</p>}

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleCsvSubmit}
                    disabled={csvUploading || !csvFile}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      csvUploading || !csvFile ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {csvUploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-gray-600">Download every zone currently in the system as a CSV file.</p>
                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleExportCsv}
                    disabled={exportLoading}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      exportLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {exportLoading ? "Exporting..." : "Download CSV"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalComponent>
      )}

      {deleteTarget && (
        <ModalComponent show={Boolean(deleteTarget)} setShow={() => setDeleteTarget(null)} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Delete Zone</h2>
            <p className="text-sm text-gray-700">
              Are you sure you want to delete zone <span className="font-semibold">{deleteTarget.zone}</span>? This cannot
              be undone.
            </p>
            {deleteTarget.location_count > 0 && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-300 rounded-md p-2">
                This zone is mapped to {deleteTarget.location_count} zone location(s) and cannot be deleted.
              </p>
            )}
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm text-white rounded bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default ZoneMasterList;
